import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { ConnectOption } from './ConnectOption';
import styles from './ConnectServiceChoices.module.scss';

/**
 * The four answers to "why is this not connected yet".
 *
 * `labelHoldsAccess` and `invitePending` file a request and send it to
 * support@music24.com, who answer the artist by email. They are for the
 * services we cannot sign in to yet; a service with a provider offers
 * the sign-in instead, because a filed request parks the row on `requested`,
 * hides the Connect button and has no way back.
 */
export enum ConnectChoice {
  signIn = 'signIn',
  labelHoldsAccess = 'labelHoldsAccess',
  invitePending = 'invitePending',
  cannotConnect = 'cannotConnect',
}

interface ConnectServiceChoicesProps {
  serviceName: string;
  choice: ConnectChoice | undefined;
  onSelect: (choice: ConnectChoice) => void;
  /** False for the services that still have no provider behind the button. */
  canSignIn: boolean;
}

const ConnectServiceChoicesComponent: React.FC<ConnectServiceChoicesProps> = ({
  serviceName,
  choice,
  onSelect,
  canSignIn,
}) => {
  const intl = useIntl();

  return (
    <div className={styles.options} role="radiogroup">
      <ConnectOption
        value={ConnectChoice.signIn}
        selected={choice}
        disabled={!canSignIn}
        onSelect={onSelect}
        title={intl.formatMessage(
          { id: 'app.sml.catalog.connect.sign_in', defaultMessage: 'Sign in with {service}' },
          { service: serviceName }
        )}
        description={
          canSignIn
            ? intl.formatMessage({
                id: 'app.sml.catalog.connect.sign_in_ready',
                defaultMessage:
                  'Read your real daily streams, listeners, saves and playlist adds. We store your sign-in encrypted and use it only to sync your numbers.',
              })
            : intl.formatMessage({
                id: 'app.sml.catalog.connect.sign_in_help',
                defaultMessage:
                  'Not built yet. Nothing on this screen waits for it, and every other option works today.',
              })
        }
      />
      {!canSignIn && (
        <>
          <ConnectOption
            value={ConnectChoice.labelHoldsAccess}
            selected={choice}
            onSelect={onSelect}
            title={intl.formatMessage({
              id: 'app.sml.catalog.connect.label',
              defaultMessage: 'My label or distributor holds the access',
            })}
            description={intl.formatMessage({
              id: 'app.sml.catalog.connect.label_help',
              defaultMessage:
                'We send your request to our support team. They email you about the next step. Meanwhile you can claim releases and promote them as normal.',
            })}
          />
          <ConnectOption
            value={ConnectChoice.invitePending}
            selected={choice}
            onSelect={onSelect}
            title={intl.formatMessage({
              id: 'app.sml.catalog.connect.invite',
              defaultMessage: 'I applied but the invite has not arrived',
            })}
            description={intl.formatMessage({
              id: 'app.sml.catalog.connect.invite_help',
              defaultMessage: 'We send your request to our support team. They email you when the connection is ready.',
            })}
          />
        </>
      )}
      <ConnectOption
        value={ConnectChoice.cannotConnect}
        selected={choice}
        onSelect={onSelect}
        title={intl.formatMessage({
          id: 'app.sml.catalog.connect.cannot',
          defaultMessage: 'I cannot connect this at all',
        })}
        description={intl.formatMessage({
          id: 'app.sml.catalog.connect.cannot_help',
          defaultMessage:
            'Fine. Paste your artist profile URL so we can read your public releases, and claim what is yours by hand.',
        })}
      />
    </div>
  );
};

export const ConnectServiceChoices = TypedMemo(ConnectServiceChoicesComponent);
